import type { PublicEnquiryFormField } from '../types/public-enquiry-form';
import { isIsoCountryName } from './iso-countries';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE = /^[+\d][\d\s().-]{5,}$/;

/**
 * A checkbox-list answer travels as one choice per line, matching what
 * `EnquiryService::validateFieldAnswers` splits on. Option labels never contain a
 * newline, so the separator is safe where a comma would not be.
 */
const MULTI_SEPARATOR = '\n';

export function splitMultiValue(value: string): string[] {
    const seen = new Set<string>();
    for (const part of value.split(/\r?\n/)) {
        const choice = part.trim();
        if (choice !== '') seen.add(choice);
    }
    return [...seen];
}

export function joinMultiValue(values: readonly string[]): string {
    return splitMultiValue(values.join(MULTI_SEPARATOR)).join(MULTI_SEPARATOR);
}

/** Whether a stored answer is the visitor's own text rather than one of the fixed choices. */
export function isOtherAnswer(
    field: Pick<PublicEnquiryFormField, 'options' | 'allowOther'>,
    value: string,
): boolean {
    return field.allowOther && value !== '' && !field.options.includes(value);
}

/**
 * Client-side mirror of `EnquiryService::validateFieldAnswers`. Returns an error
 * message per field key, empty when everything passes. The server re-checks.
 */
export function validateFieldAnswers(
    fields: PublicEnquiryFormField[],
    answers: Record<string, string>,
): Record<string, string> {
    const errors: Record<string, string> = {};
    for (const field of fields) {
        const value = (answers[field.key] ?? '').trim();
        if (value === '') {
            if (field.required) {
                errors[field.key] = requiredMessage(field.type);
            }
            continue;
        }
        const error = typeError(field, value);
        if (error) errors[field.key] = error;
    }
    return errors;
}

function requiredMessage(type: PublicEnquiryFormField['type']): string {
    switch (type) {
        case 'Select':
            return 'Please choose an option.';
        case 'MultiSelect':
            return 'Please tick at least one option.';
        case 'Country':
            return 'Please choose a country.';
        default:
            return 'This field is required.';
    }
}

function typeError(
    field: PublicEnquiryFormField,
    value: string,
): string | null {
    switch (field.type) {
        case 'Email':
            return EMAIL.test(value) ? null : 'Enter a valid email address.';
        case 'Phone':
            return PHONE.test(value) ? null : 'Enter a valid phone number.';
        case 'Url':
            try {
                const url = new URL(
                    /^https?:\/\//i.test(value) ? value : `https://${value}`,
                );
                return url.hostname.includes('.')
                    ? null
                    : 'Enter a valid web address.';
            } catch {
                return 'Enter a valid web address.';
            }
        case 'Select':
            if (field.options.includes(value)) return null;
            if (field.allowOther) {
                return value.length > 200
                    ? 'Keep this under 200 characters.'
                    : null;
            }
            return 'Please choose one of the options.';
        case 'MultiSelect': {
            const choices = splitMultiValue(value);
            if (choices.length === 0) {
                return field.required ? requiredMessage(field.type) : null;
            }
            for (const choice of choices) {
                if (field.options.includes(choice)) continue;
                if (!field.allowOther) return 'Please tick from the options.';
                if (choice.length > 200) {
                    return 'Keep your own answer under 200 characters.';
                }
            }
            return null;
        }
        case 'Country':
            return isIsoCountryName(value) ? null : 'Please choose a country.';
        case 'Text':
            return value.length > 255
                ? 'Keep this under 255 characters.'
                : null;
        case 'Textarea':
            return value.length > 2000
                ? 'Keep this under 2000 characters.'
                : null;
        default: {
            const exhaustive: never = field.type;
            return exhaustive;
        }
    }
}

/** Trimmed, non-empty answers only, so the payload never carries blanks. */
export function compactFieldAnswers(
    fields: PublicEnquiryFormField[],
    answers: Record<string, string>,
): Record<string, string> {
    const compact: Record<string, string> = {};
    for (const field of fields) {
        const value = (answers[field.key] ?? '').trim();
        if (value !== '') compact[field.key] = value;
    }
    return compact;
}
